import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MockJobService, Job } from '../../services-mock/mock-job.service';

@Component({
  selector: 'app-job-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, AvatarModule],
  template: `
    <div class="job-detail-container" *ngIf="job">
      <div class="job-header">
        <button
          pButton
          type="button"
          icon="pi pi-arrow-left"
          label="Back"
          (click)="goBack()"
          class="back-button"
        ></button>
      </div>

      <div class="job-content">
        <div class="job-image-section">
          <img [src]="job.imageUrl" [alt]="job.title" class="job-detail-image" />
        </div>

        <div class="job-info">
          <h1 class="job-title">{{ job.title }}</h1>
          <div class="job-meta">
            <div class="job-company">
              <i class="pi pi-building"></i>
              {{ job.company }}
            </div>
            <div class="job-location">
              <i class="pi pi-map-marker"></i>
              {{ job.location }}
            </div>
            <div class="job-type">
              <i class="pi pi-briefcase"></i>
              {{ job.type }}
            </div>
            <div class="job-category">
              <span class="category-badge">{{ job.category }}</span>
            </div>
          </div>

          <div class="job-salary" *ngIf="job.salary">
            <i class="pi pi-dollar"></i>
            {{ job.salary }}
          </div>

          <div class="job-actions">
            <button
              pButton
              type="button"
              label="Apply Now"
              icon="pi pi-send"
              class="apply-button"
            ></button>
            <button
              pButton
              type="button"
              label="Save Job"
              icon="pi pi-bookmark"
              class="save-button"
            ></button>
          </div>

          <div class="job-description">
            <h2>Job Description</h2>
            <p>{{ job.description }}</p>
          </div>

          <div class="job-requirements">
            <h2>Requirements</h2>
            <ul>
              <li *ngFor="let requirement of job.requirements">
                {{ requirement }}
              </li>
            </ul>
          </div>

          <div class="job-posted">
            <i class="pi pi-calendar"></i>
            Posted {{ formatDate(job.postedDate) }}
          </div>
        </div>
      </div>
    </div>

    <div class="job-detail-container" *ngIf="!job && !loading">
      <div class="error-message">
        <h2>Job Not Found</h2>
        <p>The job you're looking for doesn't exist or has been removed.</p>
        <button
          pButton
          type="button"
          label="Back to Feed"
          (click)="goBack()"
        ></button>
      </div>
    </div>
  `,
  styles: [
    `
      .job-detail-container {
        max-width: 1000px;
        margin: 0 auto;
        padding: 1.5rem;
        background-color: #f5f5f5;
        min-height: calc(100vh - 60px);
      }

      .job-header {
        margin-bottom: 1.5rem;
      }

      .back-button {
        background: transparent;
        color: #666666;
        border: 1px solid #e5e5e5;
      }

      .job-content {
        background-color: #ffffff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .job-image-section {
        width: 100%;
        height: 300px;
        overflow: hidden;
        background-color: #f5f5f5;
      }

      .job-detail-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .job-info {
        padding: 2rem;
      }

      .job-title {
        font-size: 2rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 1rem 0;
      }

      .job-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;
        margin-bottom: 1rem;
        align-items: center;
      }

      .job-company,
      .job-location,
      .job-type {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #666666;
        font-size: 0.95rem;
      }

      .job-company i,
      .job-location i,
      .job-type i {
        font-size: 0.9rem;
      }

      .category-badge {
        background-color: #e5e5e5;
        color: #333333;
        padding: 0.25rem 0.75rem;
        border-radius: 20px;
        font-size: 0.85rem;
        font-weight: 500;
      }

      .job-salary {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 1.1rem;
        font-weight: 600;
        color: #0066cc;
        margin-bottom: 1.5rem;
      }

      .job-salary i {
        font-size: 1rem;
      }

      .job-actions {
        display: flex;
        gap: 1rem;
        margin-bottom: 2rem;
        padding-bottom: 2rem;
        border-bottom: 1px solid #e5e5e5;
      }

      .apply-button {
        background: #0066cc;
        color: #ffffff;
        border: none;
      }

      .save-button {
        background: transparent;
        color: #666666;
        border: 1px solid #e5e5e5;
      }

      .job-description,
      .job-requirements {
        margin-bottom: 2rem;
      }

      .job-description h2,
      .job-requirements h2 {
        font-size: 1.5rem;
        font-weight: 600;
        color: #000000;
        margin: 0 0 1rem 0;
      }

      .job-description p {
        font-size: 1rem;
        line-height: 1.6;
        color: #333333;
        margin: 0;
      }

      .job-requirements ul {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .job-requirements li {
        padding: 0.5rem 0;
        padding-left: 1.5rem;
        position: relative;
        font-size: 1rem;
        line-height: 1.6;
        color: #333333;
      }

      .job-requirements li:before {
        content: '•';
        position: absolute;
        left: 0;
        color: #0066cc;
        font-weight: bold;
        font-size: 1.2rem;
      }

      .job-posted {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #999999;
        font-size: 0.9rem;
        padding-top: 1rem;
        border-top: 1px solid #e5e5e5;
      }

      .error-message {
        text-align: center;
        padding: 3rem;
        background-color: #ffffff;
        border-radius: 8px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .error-message h2 {
        color: #000000;
        margin-bottom: 1rem;
      }

      .error-message p {
        color: #666666;
        margin-bottom: 2rem;
      }
    `,
  ],
})
export class JobDetailComponent implements OnInit {
  job: Job | undefined;
  loading = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private jobService: MockJobService
  ) {}

  ngOnInit() {
    const jobId = this.route.snapshot.params['id'];
    if (jobId) {
      this.jobService.getJob(jobId).subscribe((job) => {
        this.job = job;
        this.loading = false;
      });
    } else {
      this.loading = false;
    }
  }

  goBack() {
    this.router.navigate(['/feed']);
  }

  formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return 'today';
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    }
  }
}

